/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IContractValue } from "../../../../../interfaces/contractValues";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { debounce } from "lodash";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  createContractValue,
  listContractValues,
  listReasons,
  showContractValue,
  updateContractValue,
  deleteContractValue,
} from "@/services";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";
const DEFAULT_DATA: IContractValue = {
  id: null,
  idContract: null,
  idCustomer: null,
  idService: null,
  type: null,
  idScope: null,
  externalCode: "",
  idItem: null,
  value: "0",
  typeSite: null,
  idRegion: null,
  valueReceveable: "0",
};

export const useContractValues = ({
  filter: filterInitial = null,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  id: idSelect,
  onCancel,
  onCreate,
  onUpdate,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  idService?: string;
  id?: string;
  typeDefault?: string;
  onCancel?: () => void;
  onCreate?: () => void;
  onUpdate?: () => void;
}) => {
  const { idContractValue: id = idSelect || "new", idContract } = useParams();
  const [contractValues, setContractValues] = useState<IContractValue[]>([]);
  const [contractValue, setContractValue] =
    useState<IContractValue>(DEFAULT_DATA);
  const { filter, setFilter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: null, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: `/towermanagement/contracts/${idContract}/contract_values`,
    pathBack: `/towermanagement/contracts/${idContract}`,
    onBack: onCancel,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleContractValue = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setContractValue((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      if (key === "type") newData["idReason"] = null;
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter: filterParam,
      }: {
        page?: number;
        limit?: number;
        filter?: Record<string, any>;
      }) => {
        setLoading(true);
        listContractValues({
          includes: [...includes],
          filter: { ...filter, ...filterParam },
          page,
          limit,
        })
          .then(
            ({
              contractValues: listContractValues,
              limit,
              currentPage,
              totalItems,
            }) => {
              setContractValues(listContractValues);
              
              setLimit(Number(limit));
              setTotalItems(Number(totalItems));
            }
          )
          .finally(() => setLoading(false));
      },
      300
    ),
    []
  );

  const search = useEntitySearch({ list: listContractValues, key: "contractValues", includes, filter });


  const searchReasons = async ({
    page = 1,
    limit,
    filter: filterG = {},
    includes: includesD = [],
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
    includes?: string[];
  }) => {
    const data = await listReasons({
      includes: [...includes, ...includesD],
      page,
      filter: { ...filter, ...filterG },
      limit,
    });
    return { items: data.reasons };
  };

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showContractValue({ id, includes: [] })
      .then(({ contractValue: contractValuesShow }) => {
        setContractValue(contractValuesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createContractValue({ contractValue })
      .then(() => {
        toast.success(`Valor criado com sucesso!`);
        onCreate();
        goBack();
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateContractValue({ id: contractValue.id!, contractValue })
      .then(() => {
        toast.success(`Valor atualizada com sucesso!`);
        if (onUpdate) {
          onUpdate();
          return;
        }
        goBack();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);

  useEffect(() => {
    // console.log(filterInitial);
    setFilter(filterInitial);
  }, [filterInitial]);

  useEffect(() => {
    if (id === "new") handleContractValue("idContract", idContract);
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteContractValue({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Valor de contrato",
        plural: "valores de contrato",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    contractValues,
    contractValue,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleContractValue,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    searchReasons,
    getAll,
  };
};
